// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Admin Page (User Management)
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type AdminUser } from '@/lib/api';
import {
  Button,
  Input,
  Badge,
  PageHeader,
  StatCard,
  Switch,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui';
import {
  Users,
  Shield,
  Crown,
  Trash2,
  Edit3,
  Check,
  UserCheck,
  UserX,
  FolderOpen,
  Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';

const LICENSE_TIERS = ['free', 'pro', 'enterprise'] as const;
const USER_ROLES = ['member', 'admin', 'cto'] as const;

const LICENSE_BADGE_VARIANT: Record<string, 'default' | 'brand' | 'info'> = {
  free: 'default',
  pro: 'brand',
  enterprise: 'info',
};

const ROLE_BADGE_VARIANT: Record<string, 'default' | 'brand' | 'warning'> = {
  member: 'default',
  admin: 'brand',
  cto: 'warning',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  cto: <Crown className="w-3 h-3" />,
  admin: <Shield className="w-3 h-3" />,
};

function EditUserModalContent({
  user,
  onSave,
}: {
  user: AdminUser;
  onSave: (data: Partial<AdminUser>) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState(user.role);
  const [licenseTier, setLicenseTier] = useState(user.licenseTier || 'pro');
  const [licenseExpiresAt, setLicenseExpiresAt] = useState(
    user.licenseExpiresAt ? user.licenseExpiresAt.slice(0, 10) : '',
  );
  const [isActive, setIsActive] = useState(user.isActive);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('admin.editUser')}</DialogTitle>
        <DialogDescription>{user.email}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <Input label={t('auth.fullName')} value={name} onChange={(e) => setName(e.target.value)} />

        <Select value={role} onValueChange={setRole}>
          <SelectTrigger label={t('admin.role')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {USER_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {r.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={licenseTier} onValueChange={setLicenseTier}>
          <SelectTrigger label={t('admin.licenseTier')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LICENSE_TIERS.map((tier) => (
              <SelectItem key={tier} value={tier}>
                {tier.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          label={t('admin.licenseExpiry')}
          type="date"
          value={licenseExpiresAt}
          onChange={(e) => setLicenseExpiresAt(e.target.value)}
          hint={t('admin.licenseExpiryHint')}
        />

        <Switch
          checked={isActive}
          onCheckedChange={setIsActive}
          label={isActive ? t('admin.accountActive') : t('admin.accountDisabled')}
        />
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <Button variant="outline">{t('common.cancel')}</Button>
        </DialogClose>
        <Button
          onClick={() =>
            onSave({
              name,
              role,
              licenseTier,
              licenseExpiresAt: licenseExpiresAt ? new Date(licenseExpiresAt).toISOString() : null,
              isActive,
            })
          }
        >
          <Check className="w-4 h-4 mr-1.5" />
          {t('common.save')}
        </Button>
      </DialogFooter>
    </>
  );
}

export default function AdminPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.adminListUsers(),
  });

  const { data: stats } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => api.adminGetStats(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AdminUser> }) =>
      api.adminUpdateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setEditingUser(null);
      toast.success(t('admin.userUpdated'));
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('common.error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.adminDeleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] });
      setDeletingUserId(null);
      toast.success(t('admin.userDeleted'));
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('common.error')),
  });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title={t('admin.title')}
        subtitle={t('admin.subtitle')}
        breadcrumb={<Shield className="w-8 h-8 text-brand-400" />}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={t('admin.totalUsers')}
          value={stats?.users?.total || 0}
          icon={<Users className="w-6 h-6" />}
          color="brand"
        />
        <StatCard
          label={t('admin.activeUsers')}
          value={stats?.users?.active || 0}
          icon={<UserCheck className="w-6 h-6" />}
          color="success"
        />
        <StatCard
          label={t('admin.totalProjects')}
          value={stats?.projects?.total || 0}
          icon={<FolderOpen className="w-6 h-6" />}
          color="info"
        />
        <StatCard
          label={t('admin.proLicenses')}
          value={(stats?.licenses?.pro || 0) + (stats?.licenses?.enterprise || 0)}
          icon={<Activity className="w-6 h-6" />}
          color="warning"
        />
      </div>

      {/* Users Table */}
      <div className="glass-card rounded-2xl border border-surface-800/60 overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-800/60">
          <h2 className="text-lg font-semibold text-surface-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-400" />
            {t('admin.allUsers')}
          </h2>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-surface-400">{t('common.loading')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-800/60 text-left">
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                    {t('admin.user')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                    {t('admin.role')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                    {t('admin.license')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                    {t('admin.status')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                    {t('admin.projects')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                    {t('admin.lastActive')}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wider text-right">
                    {t('admin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-800/40">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-surface-800/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-surface-100">{user.name}</div>
                          <div className="text-xs text-surface-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={ROLE_BADGE_VARIANT[user.role] || 'default'}>
                        {ROLE_ICONS[user.role]}
                        {user.role.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge
                        variant={LICENSE_BADGE_VARIANT[user.licenseTier || 'free'] || 'default'}
                      >
                        {(user.licenseTier || 'free').toUpperCase()}
                      </Badge>
                      {user.licenseExpiresAt && (
                        <div className="text-[10px] text-surface-500 mt-0.5">
                          {t('admin.expiresOn', {
                            date: new Date(user.licenseExpiresAt).toLocaleDateString(),
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={user.isActive ? 'success' : 'danger'}>
                        {user.isActive ? (
                          <UserCheck className="w-3 h-3 mr-1" />
                        ) : (
                          <UserX className="w-3 h-3 mr-1" />
                        )}
                        {user.isActive ? t('admin.active') : t('admin.disabled')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-300">{user.projectCount || 0}</td>
                    <td className="px-6 py-4 text-xs text-surface-500">
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : t('admin.never')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => setEditingUser(user)}
                          title={t('common.edit')}
                          className="hover:text-brand-400 hover:bg-brand-500/10"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          onClick={() => setDeletingUserId(user.id)}
                          title={t('common.delete')}
                          className="hover:text-danger hover:bg-danger/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open: boolean) => !open && setEditingUser(null)}>
        <DialogContent>
          {editingUser && (
            <EditUserModalContent
              user={editingUser}
              onSave={(data) => updateMutation.mutate({ id: editingUser.id, data })}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deletingUserId !== null}
        onOpenChange={(open: boolean) => !open && setDeletingUserId(null)}
      >
        <DialogContent className="max-w-sm text-center">
          <DialogHeader className="items-center">
            <div className="w-14 h-14 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-2">
              <Trash2 className="w-7 h-7 text-danger" />
            </div>
            <DialogTitle>{t('admin.confirmDelete')}</DialogTitle>
            <DialogDescription>{t('admin.confirmDeleteDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <DialogClose asChild>
              <Button variant="outline">{t('common.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              onClick={() => deletingUserId !== null && deleteMutation.mutate(deletingUserId)}
              loading={deleteMutation.isPending}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
